/**
 * hooks.ts — useSkuMigration hook — all state, data fetching, and submit logic
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { message } from 'antd';
import { apiService } from '../../services/api';
import {
  SKU,
  ProductSKUConfig,
  ProductSKUConfigEntitlement,
  SKUOrgEntitlementConfig,
} from '../../types';
import {
  ConversionType,
  SkuMigrationRow,
  MigrationFilters,
  UseSkuMigrationReturn,
  RawSku,
  MigrationConfigSummary,
} from './types';
import { CONVERSION_BUYING_MODEL_FILTER, TARGET_BUYING_MODEL } from './constants';
import { sanitizeTextInput } from '../../utils/utils';

// ─── helpers ──────────────────────────────────────────────────────────────────

function getBuyingModel(sku: SKU): string {
  const raw = sku as unknown as Record<string, unknown>;
  return (raw.subscription_buying_model || raw.SubscriptionBuyingModel || '') as string;
}

function getIsExternal(sku: SKU): boolean {
  const raw = sku as unknown as Record<string, unknown>;
  return (raw.is_external ?? raw.IsExternal ?? false) as boolean;
}

// ─── hook ─────────────────────────────────────────────────────────────────────

export function useSkuMigration(): UseSkuMigrationReturn {
  // ── step navigation ──────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(0);

  // ── loading flags ────────────────────────────────────────────────────────
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // ── raw API data ─────────────────────────────────────────────────────────
  const [skus, setSkus] = useState<SKU[]>([]);
  const allPscRef  = useRef<ProductSKUConfig[]>([]);
  const allPsceRef = useRef<ProductSKUConfigEntitlement[]>([]);
  const allSoecRef = useRef<SKUOrgEntitlementConfig[]>([]);

  // ── filter state (screen 2) ───────────────────────────────────────────────
  const defaultConversionType: ConversionType = 'none';
  const [filters, setFiltersRaw] = useState<MigrationFilters>({
    conversionType:    defaultConversionType,
    buyingModelFilter: CONVERSION_BUYING_MODEL_FILTER[defaultConversionType],
    searchText:        '',
  });

  // ── selection state (screen 2) ────────────────────────────────────────────
  const [selectedSkuIds, setSelectedSkuIds] = useState<number[]>([]);

  // ── csv import map — sourceSkuId → targetSkuName ─────────────────────────
  const [csvImportMap, setCsvImportMap] = useState<Record<number, string>>({});
  const [csvImported,  setCsvImported]  = useState(false);

  // ── name-mapping state (screen 3) ────────────────────────────────────────
  const [migrationRows, setMigrationRows] = useState<SkuMigrationRow[]>([]);
  const [rowErrors,     setRowErrors]     = useState<Record<number, string>>({});

  // ── data fetching ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [skuList, pscList, psceList, soecList] = await Promise.all([
          apiService.getSKUs(),
          apiService.getProductSkuConfigs(),
          apiService.getProductSkuConfigEntitlements(),
          apiService.getSKUOrgEntitlementConfigs(),
        ]);
        if (cancelled) { return; }
        setSkus(skuList);
        allPscRef.current = pscList;
        allPsceRef.current = psceList;
        allSoecRef.current = soecList;
      } catch {
        if (!cancelled) { message.error('Failed to load SKU data. Please refresh and try again.'); }
      } finally {
        if (!cancelled) { setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── filter handler — resets selection when conversion type changes ─────────
  const setFilters = useCallback((f: MigrationFilters) => {
    setFiltersRaw((prev) => {
      if (f.conversionType !== prev.conversionType) {
        setSelectedSkuIds([]);
        setCsvImportMap({});
        setCsvImported(false);
      }
      return f;
    });
  }, []);

  // ── derived: filtered SKU list ────────────────────────────────────────────
  const filteredSkus: RawSku[] = useMemo(() => {
    const bm = filters.buyingModelFilter;
    const q  = filters.searchText.trim().toLowerCase();
    return skus
      .filter((s) => {
        if (bm !== 'all') {
          if (getBuyingModel(s).toUpperCase() !== bm) { return false; }
        }
        if (q) { return sanitizeTextInput(s.name).toLowerCase().includes(q); }
        return true;
      })
      .map((s) => ({
        id:                        s.id ?? 0,
        name:                      sanitizeTextInput(s.name),
        subscription_buying_model: getBuyingModel(s),
        is_external:               getIsExternal(s),
      }));
  }, [skus, filters]);

  // ── selection handlers ────────────────────────────────────────────────────
  const toggleSkuSelection = useCallback((id: number) => {
    setSelectedSkuIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const toggleSelectAll = useCallback((ids: number[]) => {
    setSelectedSkuIds((prev) => {
      const allSelected = ids.every((id) => prev.includes(id));
      return allSelected
        ? prev.filter((id) => !ids.includes(id))
        : Array.from(new Set([...prev, ...ids]));
    });
  }, []);

  // ── CSV import ────────────────────────────────────────────────────────────
  const importCsv = useCallback(
    (file: File): Promise<{ matched: number; unmatched: string[]; wrongModel: string[] }> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = (e) => {
          const text = (e.target?.result as string) ?? '';
          const lines = text.split(/\r?\n/).filter((l) => l.trim());
          if (!lines.length) { resolve({ matched: 0, unmatched: [], wrongModel: [] }); return; }

          // Skip header row if it contains 'sku_name'
          const dataLines = lines[0].toLowerCase().includes('sku_name')
            ? lines.slice(1)
            : lines;

          // Determine the required buying model for this conversion type.
          // 'clone' accepts all; 'alc_to_ea' requires ALC; 'ea_to_alc' requires EA.
          const requiredModel = CONVERSION_BUYING_MODEL_FILTER[filters.conversionType];

          const newMap: Record<number, string> = {};
          const newIds: number[] = [];
          const unmatched:  string[] = [];
          const wrongModel: string[] = [];

          for (const line of dataLines) {
            if (!line.trim()) { continue; }
            const commaIdx = line.indexOf(',');
            if (commaIdx === -1) { continue; }
            const srcName    = sanitizeTextInput(line.slice(0, commaIdx).trim());
            const targetName = sanitizeTextInput(line.slice(commaIdx + 1).trim());

            const found = skus.find(
              (s) => sanitizeTextInput(s.name).toLowerCase() === srcName.toLowerCase(),
            );

            if (!found || found.id === undefined) {
              unmatched.push(srcName);
              continue;
            }

            // Check buying model only when conversion type is not 'clone'
            if (requiredModel !== 'all') {
              const skuModel = getBuyingModel(found).toUpperCase();
              if (skuModel !== requiredModel) {
                wrongModel.push(srcName);
                continue;
              }
            }

            newMap[found.id] = targetName;
            newIds.push(found.id);
          }

          setCsvImportMap(newMap);
          // Only mark as successfully imported when at least one SKU matched
          if (newIds.length > 0) {
            setCsvImported(true);
          }
          setSelectedSkuIds((prev) => Array.from(new Set([...prev, ...newIds])));
          resolve({ matched: newIds.length, unmatched, wrongModel });
        };
        reader.readAsText(file);
      }),
    [skus, filters.conversionType],
  );

  // ── name mapping ──────────────────────────────────────────────────────────
  const buildMigrationRows = useCallback(() => {
    setMigrationRows(
      selectedSkuIds.map((id) => {
        const sku = skus.find((s) => s.id === id);
        return {
          sourceSkuId:       id,
          sourceSkuName:     sku ? sanitizeTextInput(sku.name) : '',
          sourceBuyingModel: sku ? getBuyingModel(sku) : '',
          // Use CSV-imported target name if available, otherwise pre-fill with source name
          targetSkuName:     sanitizeTextInput(csvImportMap[id] ?? sku?.name ?? ''),
        };
      }),
    );
    setRowErrors({});
  }, [selectedSkuIds, skus, csvImportMap]);

  const updateTargetName = useCallback((sourceSkuId: number, name: string) => {
    const sanitizedName = sanitizeTextInput(name);
    setMigrationRows((prev) =>
      prev.map((r) => (r.sourceSkuId === sourceSkuId ? { ...r, targetSkuName: sanitizedName } : r)),
    );
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[sourceSkuId];
      return next;
    });
  }, []);

  // ── validation ────────────────────────────────────────────────────────────
  const validateNames = useCallback((): boolean => {
    const errors: Record<number, string> = {};
    const existingNames = new Set(skus.map((s) => sanitizeTextInput(s.name).trim().toLowerCase()));
    const seen = new Set<string>();
    for (const row of migrationRows) {
      const t = sanitizeTextInput(row.targetSkuName).trim();
      if (!t) {
        errors[row.sourceSkuId] = 'Name is required';
      } else if (existingNames.has(t.toLowerCase())) {
        errors[row.sourceSkuId] = 'A SKU with this name already exists';
      } else if (seen.has(t.toLowerCase())) {
        errors[row.sourceSkuId] = 'Duplicate name within this migration';
      } else {
        seen.add(t.toLowerCase());
      }
    }
    setRowErrors(errors);
    return Object.keys(errors).length === 0;
  }, [migrationRows, skus]);

  // ── canGoNext ─────────────────────────────────────────────────────────────
  const canGoNext = useMemo(() => {
    if (currentStep === 0) {
      return filters.conversionType !== 'none' && selectedSkuIds.length > 0;
    }
    if (currentStep === 1) {
      if (!migrationRows.length) { return false; }
      const seen = new Set<string>();
      for (const row of migrationRows) {
        const t = sanitizeTextInput(row.targetSkuName).trim();
        // Must be non-empty, different from the source name, and unique within batch
        if (!t) { return false; }
        if (t.toLowerCase() === row.sourceSkuName.toLowerCase()) { return false; }
        if (seen.has(t.toLowerCase())) { return false; }
        seen.add(t.toLowerCase());
      }
      return true;
    }
    return true;
  }, [currentStep, filters.conversionType, selectedSkuIds, migrationRows]);

  // ── step navigation ───────────────────────────────────────────────────────
  const goNext = useCallback((onAdvance?: () => void) => {
    if (currentStep === 0) {
      if (filters.conversionType === 'none') {
        message.warning('Please select a conversion type to continue.');
        return;
      }
      if (!selectedSkuIds.length) { message.warning('Select at least one SKU to continue.'); return; }
      buildMigrationRows();
      setCurrentStep(1);
      if (onAdvance) { onAdvance(); }
    } else if (currentStep === 1) {
      if (!validateNames()) { return; }
      setCurrentStep(2);
      if (onAdvance) { onAdvance(); }
    }
  }, [currentStep, filters.conversionType, selectedSkuIds, buildMigrationRows, validateNames]);

  const goPrev = useCallback((onRetreat?: () => void) => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
      if (onRetreat) { onRetreat(); }
    }
  }, [currentStep]);

  // ── config summary (review screen) ───────────────────────────────────────
  const configSummary: MigrationConfigSummary[] = useMemo(() => {
    if (!migrationRows.length) { return []; }
    const maxSkuId = Math.max(...skus.map((s) => s.id ?? 0), 0);
    return migrationRows.map((row, idx) => {
      const targetBuyingModel =
        TARGET_BUYING_MODEL[filters.conversionType] || row.sourceBuyingModel;
      return {
        sourceSkuId:                      row.sourceSkuId,
        sourceSkuName:                    row.sourceSkuName,
        targetSkuName:                    sanitizeTextInput(row.targetSkuName).trim(),
        targetBuyingModel,
        newSkuId:                         maxSkuId + idx + 1,
        productSkuConfigCount: allPscRef.current.filter(
          (c) => c.sku_id === row.sourceSkuId,
        ).length,
        productSkuConfigEntitlementCount: allPsceRef.current.filter(
          (e) => e.product_sku_config_sku_id === row.sourceSkuId,
        ).length,
        skuOrgEntitlementConfigCount: allSoecRef.current.filter(
          (c) => c.sku_id === row.sourceSkuId,
        ).length,
      };
    });
  }, [migrationRows, skus, filters.conversionType]);

  // ── submit ────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async (onSuccess?: () => void) => {
    if (!validateNames()) { return; }
    setSubmitting(true);
    try {
      const maxSkuId = Math.max(...skus.map((s) => s.id ?? 0), 0);

      const skuEntries: Array<{ data: Record<string, unknown> }> = [];
      const pscEntries: Array<{ data: Record<string, unknown> }> = [];
      const psceEntries: Array<{ data: Record<string, unknown> }> = [];
      const soecEntries: Array<{ data: Record<string, unknown> }> = [];

      migrationRows.forEach((row, idx) => {
        const newSkuId      = maxSkuId + idx + 1;
        const sourceSku = skus.find((s) => s.id === row.sourceSkuId);
        const targetBuying = TARGET_BUYING_MODEL[filters.conversionType] || getBuyingModel(sourceSku ?? ({} as SKU));

        // ① New SKU
        skuEntries.push({
          data: {
            ID:                      newSkuId,
            Name:                    sanitizeTextInput(row.targetSkuName).trim(),
            IsExternal:              sourceSku ? getIsExternal(sourceSku) : false,
            SubscriptionBuyingModel: targetBuying,
          },
        });

        // ② product_sku_configs
        allPscRef.current
          .filter((c) => c.sku_id === row.sourceSkuId)
          .forEach((c) => {
            pscEntries.push({
              data: {
                ProductID:     c.product_id,
                SkuID:         newSkuId,
                ...(c.operation               ? { Operation:       c.operation }       : {}),
                ...(c.operand !== null && c.operand !== undefined ? { Operand: c.operand } : {}),
                ...(c.force_region_name       ? { ForceRegionName: c.force_region_name } : {}),
              },
            });
          });

        // ③ product_sku_config_entitlements
        allPsceRef.current
          .filter((e) => e.product_sku_config_sku_id === row.sourceSkuId)
          .forEach((e) => {
            psceEntries.push({
              data: {
                ProductSkuConfigProductID: e.product_sku_config_product_id,
                ProductSkuConfigSkuID:     newSkuId,
                EntitlementID:             e.entitlement_id,
              },
            });
          });

        // ④ sku_org_entitlement_configs
        allSoecRef.current
          .filter((c) => c.sku_id === row.sourceSkuId)
          .forEach((c) => {
            soecEntries.push({
              data: {
                SkuID:            newSkuId,
                OrgEntitlementID: c.org_entitlement_id,
              },
            });
          });
      });

      const bulkEntry = [
        { table_key: 'skus', entries: skuEntries },
        ...(pscEntries.length  ? [{ table_key: 'product_sku_configs',             entries: pscEntries }]  : []),
        ...(psceEntries.length ? [{ table_key: 'product_sku_config_entitlements', entries: psceEntries }] : []),
        ...(soecEntries.length ? [{ table_key: 'sku_org_entitlement_configs',     entries: soecEntries }] : []),
      ];

      // One request ID is generated for this submit and attached to every
      // API call issued from within the block below (via the ApiService
      // request interceptor), so the backend can correlate all resulting
      // downstream work under one audit trail.
      const { requestId, result: response } = await apiService.runOperation(() =>
        apiService.bulkInsert({ bulk_entry: bulkEntry }),
      );
      message.success(
        `${(response as unknown as { message?: string }).message ||
          `Successfully migrated ${migrationRows.length} SKU(s).`} Request ID: ${requestId}`,
      );

      // Reset to initial state
      setCurrentStep(0);
      setSelectedSkuIds([]);
      setMigrationRows([]);
      setRowErrors({});
      setFiltersRaw({
        conversionType:    defaultConversionType,
        buyingModelFilter: CONVERSION_BUYING_MODEL_FILTER[defaultConversionType],
        searchText:        '',
      });
      if (onSuccess) { onSuccess(); }
    } catch (error: unknown) {
      const err = error as Record<string, { data?: { message?: string } }>;
      message.error(err.response?.data?.message || 'Migration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [migrationRows, skus, filters.conversionType, validateNames, defaultConversionType]);

  return {
    loading,
    submitting,
    currentStep,
    goNext,
    goPrev,
    filters,
    setFilters,
    filteredSkus,
    selectedSkuIds,
    toggleSkuSelection,
    toggleSelectAll,
    importCsv,
    csvImported,
    migrationRows,
    updateTargetName,
    rowErrors,
    canGoNext,
    handleSubmit,
    configSummary,
  };
}
